import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import SuperFundService from '@/services/SuperFundService.ts'
import Investments from '@/types/api/super/InvestmentOption.ts'
import { IntrafundAdviceRequestType, TermDepositResponse } from '@/types/CallcentrePortalTypes.ts'
import { InvestmentOption } from '@/components/SuperWidget/SuperWidgetAdviceType.ts'
import { useSuperStore } from '@/store/pinia/SuperStore.ts'

export enum ModuleStatusEnum {
  adviceReady = 'advice_ready',
  processing = 'processing',
  actioned = 'actioned',
  infoNeeded = 'info_needed',
}

/**
 * TODO: This is Module 1 store (InvestmentOptionsInsideSuper - Intrafund Advice)
 * Super link up for User
 */
// expecting this to become snake_cased
export type SuperWidgetData = {
  condition: string
  options: InvestmentOption[]
  intrafundRecommendationId: number
  recommendationTexts?: Record<string, any>
}

export const useIntrafundAdviceStore = defineStore('intrafundAdviceStore', () => {
  const termDepositConsent = ref<TermDepositResponse>()
  const superWidgetGraphData = ref<SuperWidgetData>()
  const superWidgetFieldsLoading = ref<boolean>(false)

  const superStore = useSuperStore()
  const requestedAllocation = ref<Array<Investments>>([
    {
      chant_west_investment_name: '',
      chant_west_investment_id: 0,
      percent_allocation: 100,
      is_cash_option: false,
      is_term_deposit: false,
      has_cash_option: false,
      restricted_product: false,
    },
  ])
  const supersArray = computed(() => useSuperStore().getSupersArray)
  const activeSuperObject = computed(() => useSuperStore().getActiveSuperObject)
  const getTermDepositConsent = computed(() => termDepositConsent.value)
  const getRequestedAllocation = computed(() => requestedAllocation.value)
  const getSuperWidgetGraphData = computed(() => superWidgetGraphData.value)
  const getSuperWidgetFieldsLoading = computed(() => superWidgetFieldsLoading.value)
  const getRecommendationTexts = computed(() => superWidgetGraphData.value?.recommendationTexts || {})
  const getHasCashAndTermDepositInvestments = computed(() => {
    const cashOrTdInvestments = supersArray.value.filter((superannuation) => {
      return superannuation.investments.find((allocation) => {
        return allocation.has_cash_option
      })
    })
    return cashOrTdInvestments.length > 0
  })

  const isAdviceLocked = computed(() =>
    [ModuleStatusEnum.processing, ModuleStatusEnum.actioned].includes(
      getSuperAdviceStage(
        superStore.getActiveSuperObject.advice_request?.advice_stage || 0,
      ) as ModuleStatusEnum,
    ),
  )

  const getSuperAdviceStage = (stage: number): string => {
    switch (stage) {
      case 3:
        return ModuleStatusEnum.adviceReady
      case 4:
        return ModuleStatusEnum.processing
      case 5:
        return ModuleStatusEnum.actioned
      default:
        return ModuleStatusEnum.infoNeeded
    }
  }

  const termDepositConsentStatus = computed(() => {
    const state = {
      waiting: false,
      notGiven: false,
      given: false,
    }

    if (!termDepositConsent.value) return state

    const { has_term_deposit, answered, consented, super_id } = termDepositConsent.value

    if (activeSuperObject.value.id === super_id) {
      state.waiting = has_term_deposit && !answered && !consented
      state.notGiven = has_term_deposit && answered && !consented
      state.given = has_term_deposit && answered && consented
    }

    return state
  })

  const setTermDepositConsent = async (consented: boolean) => {
    if (activeSuperObject.value !== undefined) {
      const payload = {
        superId: activeSuperObject.value?.id,
        consented: consented,
      }
      const response = await SuperFundService.setTermDepositConsentFlag(payload)
      termDepositConsent.value = response.data
    }
  }

  const updateTermDepositConsent = (value: TermDepositResponse) => {
    termDepositConsent.value = value
  }

  const fetchTermDepositConsent = async () => {
    if (activeSuperObject.value?.id) {
      const response = await SuperFundService.getTermDepositConsentFlag(activeSuperObject.value.id)
      termDepositConsent.value = response.data
    }
  }

  const setSuperWidgetFieldsLoading = (value: boolean) => {
    superWidgetFieldsLoading.value = value
  }

  const getModularisedSuperAdvice = (payload: { superId: string }) => {
    // Whitelabel Id is set automatically for all endpoints
    return SuperFundService.getModularisedSuperAdvice(payload.superId)
  }

  const setRequestedAllocation = (payload: Array<Investments>) => {
    requestedAllocation.value = payload
  }

  const setSuperWidgetGraphData = (value: SuperWidgetData) => {
    superWidgetGraphData.value = value
  }

  const updateIntrafundAdviceRequest = (payload: IntrafundAdviceRequestType) => {
    return SuperFundService.updateIntrafundAdviceRequest(payload, activeSuperObject.value.id)
  }

  /**
   * Get recommendation text by identifier - texts are now pre-rendered by backend
   */
  const getRecommendationText = (identifier: string): string => {
    const recommendationTexts = getRecommendationTexts.value
    
    // Backend now returns fully rendered text, so we just return it directly
    if (recommendationTexts[identifier]) {
      return String(recommendationTexts[identifier]) // Backend returns string, not array
    }
    
    return '' // Return identifier as fallback
  }

  /**
   * Resets the store state to its default values.
   */
  const resetSuperInvestmentAdvice = () => {
    termDepositConsent.value = undefined
    superWidgetGraphData.value = undefined
    superWidgetFieldsLoading.value = false
    requestedAllocation.value = [
      {
        chant_west_investment_name: '',
        chant_west_investment_id: 0,
        percent_allocation: 100,
        is_cash_option: false,
        is_term_deposit: false,
        has_cash_option: false,
        restricted_product: false,
      },
    ]
  }

  return {
    getTermDepositConsent,
    getHasCashAndTermDepositInvestments,
    getRequestedAllocation,
    getSuperWidgetFieldsLoading,
    getSuperWidgetGraphData,
    getRecommendationTexts,
    getRecommendationText,
    fetchTermDepositConsent,
    getModularisedSuperAdvice,
    getSuperAdviceStage,
    isAdviceLocked,
    setTermDepositConsent,
    setRequestedAllocation,
    setSuperWidgetFieldsLoading,
    setSuperWidgetGraphData,
    termDepositConsent,
    termDepositConsentStatus,
    updateTermDepositConsent,
    updateIntrafundAdviceRequest,
    resetSuperInvestmentAdvice,
  }
})
